<!-- The treasurer's account manager: join requests, and every account with its groups and devices. -->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import * as P from "../settings/passkeys.ts";
  import { fill, shows } from "../settings/index.ts";
  import { api } from "../lib/api.ts";
  import { app } from "../lib/store.svelte.ts";
  import { colorOf } from "../lib/colors.ts";
  import { shortDateTime, signInName } from "../lib/format.ts";
  import JoinRequests from "../components/JoinRequests.svelte";
  import DataCheck from "../components/DataCheck.svelte";
  import Drawer from "../components/Drawer.svelte";

  interface Account {
    id: string;
    name: string;
    username: string | null;
    role: "owner" | "member";
    createdAt: string;
    lastSeen: string | null;
    passkeys: number;
    sessions: number;
    /** When their unused passkey link stops working; null when there's none. */
    passkeyLinkUntil: string | null;
    groups: { groupId: string; groupName: string; personId: string; personName: string; color: string }[];
  }

  let accounts = $state<Account[] | null>(null);
  let q = $state("");
  let filter = $state<"all" | "unlinked" | "members">("all");
  let open = $state<Account | null>(null);
  let linkGroup = $state("");
  let linkPerson = $state("");
  let people = $state<{ id: string; name: string; color: string; taken: boolean }[]>([]);
  let error = $state("");

  async function load() {
    try {
      accounts = (await api<{ accounts: Account[] }>("/api/admin/accounts")).accounts;
      if (open) open = accounts.find((a) => a.id === open!.id) ?? null;
      error = "";
    } catch (e) {
      error = (e as Error).message;
    }
  }
  $effect(() => {
    void load();
  });

  const shown = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return (accounts ?? []).filter(
      (a) =>
        (filter === "all" || (filter === "unlinked" ? a.role === "member" && a.groups.length === 0 : a.role === "member")) &&
        (!needle ||
          a.name.toLowerCase().includes(needle) ||
          (a.username ?? "").includes(needle) ||
          a.groups.some((g) => g.personName.toLowerCase().includes(needle) || g.groupName.toLowerCase().includes(needle))),
    );
  });
  const counts = $derived({
    members: (accounts ?? []).filter((a) => a.role === "member").length,
    unlinked: (accounts ?? []).filter((a) => a.role === "member" && a.groups.length === 0).length,
  });

  // People in the chosen group, marking who already has an account.
  $effect(() => {
    const gid = linkGroup;
    people = [];
    linkPerson = "";
    if (!gid) return;
    void app.groupSnapshot(gid).then((s) => {
      if (!s || gid !== linkGroup) return;
      const taken = new Set((accounts ?? []).flatMap((a) => a.groups.filter((g) => g.groupId === gid).map((g) => g.personId)));
      people = s.personOrder
        .map((id) => s.people[id]!)
        .filter((p) => p && !p.archived)
        .map((p) => ({ id: p.id, name: p.name, color: p.color, taken: taken.has(p.id) }));
    });
  });

  async function act(path: string, body: unknown, done: string, method?: string) {
    try {
      await api(path, { method, body });
      app.toast(done);
      await load();
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }

  /** Link to someone already in the group, or add them to it as someone new. */
  const link = (a: Account) => {
    const g = app.groups.find((x) => x.id === linkGroup);
    const done = () => (linkGroup = "");
    if (linkPerson === NEW)
      return act(`/api/admin/accounts/${a.id}/groups`, { groupId: linkGroup, in: true, asNew: true }, `${a.name} is in ${g?.name ?? "the group"} now, added as a new person`).then(done);
    return act(`/api/admin/accounts/${a.id}/link`, { groupId: linkGroup, personId: linkPerson }, `${a.name} can now see their items`).then(done);
  };
  const NEW = "__new";

  /** A one-time link that adds a passkey to their account: share it, or copy it. */
  async function sendPasskeyLink(a: Account) {
    try {
      const { path } = await api<{ path: string }>(`/api/admin/accounts/${a.id}/passkey-link`, { body: {} });
      const url = location.origin + path;
      await load();
      if (navigator.share) {
        await navigator.share({ title: P.LINK_SHARE_TITLE, text: fill(P.LINK_SHARE_TEXT, { url }), url }).catch(() => {});
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        app.toast(fill(P.LINK_COPIED_TOAST, { name: a.name }));
      } catch {
        app.toast(fill(P.LINK_COPY_FAILED, { url }), "error");
      }
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Accounts</h1>
      <p class="lede">
        {#if accounts}
          {counts.members} member {counts.members === 1 ? "account" : "accounts"}{counts.unlinked ? `, ${counts.unlinked} not linked to anyone yet` : ""}.
        {:else}
          Everyone who can sign in, and what they can see.
        {/if}
      </p>
    </div>
  </div>

  <JoinRequests onchange={() => void load()} />
  <DataCheck />

  <div class="tools">
    <input class="input" type="search" placeholder="Search names, usernames, groups" bind:value={q} aria-label="Search accounts" />
    <div class="seg" role="group" aria-label="Show">
      <button aria-pressed={filter === "all"} onclick={() => (filter = "all")}>All</button>
      <button aria-pressed={filter === "members"} onclick={() => (filter = "members")}>Members</button>
      <button aria-pressed={filter === "unlinked"} onclick={() => (filter = "unlinked")}>Not linked</button>
    </div>
  </div>

  {#if error}<p class="owes">{error}</p>{/if}

  <div class="table" role="table" aria-label="Accounts">
    <div class="tr head" role="row">
      <span role="columnheader">Account</span>
      <span role="columnheader">Sees</span>
      <span role="columnheader" class="hide-sm">Devices</span>
      <span role="columnheader" class="hide-sm">Last active</span>
    </div>
    {#each shown as a (a.id)}
      <button class="tr" role="row" onclick={() => (open = a)}>
        <span class="who" role="cell">
          <strong>{a.name}</strong>
          <span class="muted small">{signInName(a.name, a.username) ? `Signs in as ${signInName(a.name, a.username)}` : "No sign-in name"}{a.role === "owner" ? " · treasurer" : ""}</span>
        </span>
        <span class="chips" role="cell">
          {#if a.role === "owner"}
            <span class="muted small">Everything</span>
          {:else if a.groups.length === 0}
            <span class="pill warn">Nothing yet</span>
          {:else}
            {#each a.groups as g (g.groupId)}
              <span class="chip" style:--c={colorOf(g.color)}><span class="dot"></span>{g.personName} · {g.groupName}</span>
            {/each}
          {/if}
        </span>
        <span class="muted small hide-sm" role="cell">{a.passkeys} {a.passkeys === 1 ? "passkey" : "passkeys"}</span>
        <span class="muted small hide-sm" role="cell">{a.lastSeen ? shortDateTime(a.lastSeen) : "Never"}</span>
      </button>
    {:else}
      <p class="empty">{accounts ? "No accounts match." : "Loading…"}</p>
    {/each}
  </div>
</div>

{#if open}
  <!-- The fresh copy after a change, so a group added here shows straight away. -->
  {@const a = accounts?.find((x) => x.id === open?.id) ?? open}
  <Drawer title={a.name} onclose={() => (open = null)}>
    <div class="detail">
      <p class="muted">
        {signInName(a.name, a.username) ? `Signs in as ${signInName(a.name, a.username)}` : "No sign-in name"} · {a.role === "owner" ? "Treasurer" : "Member"} · joined {shortDateTime(a.createdAt)}<br />
        {a.passkeys} {a.passkeys === 1 ? "passkey" : "passkeys"}, signed in on {a.sessions} {a.sessions === 1 ? "device" : "devices"}.
      </p>

      {#if a.role === "member"}
        <section>
          <h3>Groups</h3>
          {#each a.groups as g (g.groupId)}
            <div class="grow" style:--c={colorOf(g.color)}>
              <span class="dot"></span>
              <span><strong>{g.personName}</strong> in {g.groupName}</span>
              <button class="btn small quiet danger" onclick={() => act(`/api/admin/accounts/${a.id}/unlink`, { groupId: g.groupId }, `Removed ${a.name} from ${g.groupName}`)}>Remove</button>
            </div>
          {:else}
            {#if H.ACCOUNT_UNLINKED_NOTE.trim()}<p class="muted small">{H.ACCOUNT_UNLINKED_NOTE}</p>{/if}
          {/each}
          <div class="link">
            <select class="input" bind:value={linkGroup} aria-label="Group">
              <option value="">Add to a group…</option>
              {#each app.groups.filter((g) => !a.groups.some((x) => x.groupId === g.id)) as g (g.id)}<option value={g.id}>{g.name}</option>{/each}
            </select>
            {#if linkGroup}
              <select class="input" bind:value={linkPerson} aria-label="Person">
                <option value="">Who are they?</option>
                <option value={NEW}>Someone new: add {a.name}</option>
                {#each people as p (p.id)}<option value={p.id} disabled={p.taken}>{p.name}{p.taken ? " (has an account)" : ""}</option>{/each}
              </select>
              <button class="btn small primary" disabled={!linkPerson} onclick={() => link(a)}>{linkPerson === NEW ? "Add" : "Link"}</button>
            {/if}
          </div>
        </section>
      {/if}

      <section>
        <h3>Devices</h3>
        <div class="row">
          <button class="btn small primary" onclick={() => sendPasskeyLink(a)}>{a.passkeyLinkUntil ? P.LINK_REMAKE_BUTTON : P.LINK_MAKE_BUTTON}</button>
          {#if a.passkeyLinkUntil}
            <button class="btn small quiet" onclick={() => act(`/api/admin/accounts/${a.id}/passkey-link`, {}, fill(P.LINK_OFF_TOAST, { name: a.name }), "DELETE")}>{P.LINK_OFF_BUTTON}</button>
          {/if}
        </div>
        {#if a.passkeyLinkUntil && shows(P.LINK_OPEN_TEXT)}
          <p class="muted small">{fill(P.LINK_OPEN_TEXT, { until: shortDateTime(a.passkeyLinkUntil) })}</p>
        {/if}
        <div class="row">
          <button class="btn small" onclick={() => act(`/api/admin/accounts/${a.id}/sign-out`, {}, `Signed ${a.name} out everywhere`)}>Sign out everywhere</button>
          {#if a.role === "member"}
            <button class="btn small" onclick={() => act(`/api/admin/accounts/${a.id}/reset-passkeys`, {}, fill(P.LINK_RESET_TOAST, { name: a.name }))}>Remove passkeys (lost phone)</button>
          {/if}
        </div>
      </section>

      {#if a.role === "member"}
        <section>
          <h3>Remove account</h3>
          {#if H.ACCOUNT_DELETE_NOTE.trim()}<p class="muted small">{H.ACCOUNT_DELETE_NOTE}</p>{/if}
          <button class="btn small danger" onclick={() => act(`/api/admin/accounts/${a.id}`, {}, `Removed ${a.name}'s account`, "DELETE").then(() => (open = null))}>Remove {a.name}'s account</button>
        </section>
      {/if}
    </div>
  </Drawer>
{/if}

<style>
  .tools {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 14px;
  }

  .tools .input {
    flex: 1 1 260px;
    max-width: 420px;
  }

  .seg {
    display: inline-flex;
    gap: 2px;
    padding: 3px;
    border-radius: 10px;
    background: var(--wash);
  }

  .seg button {
    padding: 6px 12px;
    border: 0;
    border-radius: 8px;
    background: none;
    color: var(--ink-2);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 600;
    cursor: pointer;
  }

  .seg button[aria-pressed="true"] {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.12);
  }

  .table {
    border-radius: var(--r-lg);
    border: 1px solid var(--rule);
    background: var(--surface);
    overflow: hidden;
  }

  .tr {
    display: grid;
    grid-template-columns: minmax(160px, 1.2fr) 2fr 110px 150px;
    gap: 12px;
    align-items: center;
    width: 100%;
    padding: 12px 16px;
    border: 0;
    border-top: 1px solid var(--rule);
    background: none;
    color: var(--ink);
    font: inherit;
    text-align: left;
  }

  button.tr {
    cursor: pointer;
  }

  button.tr:hover {
    background: var(--wash);
  }

  .tr.head {
    border-top: 0;
    font-size: var(--t-xs);
    font-weight: 650;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .who {
    display: grid;
    min-width: 0;
  }

  .small {
    font-size: var(--t-xs);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--c) 14%, transparent);
    font-size: var(--t-xs);
    font-weight: 600;
  }

  .dot {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--c);
  }

  .pill {
    padding: 2px 8px;
    border-radius: 999px;
    background: var(--wash);
    font-size: var(--t-xs);
    font-weight: 650;
  }

  .pill.warn {
    color: var(--owes, #c2410c);
  }

  .empty {
    padding: 16px;
    margin: 0;
  }

  .detail {
    display: grid;
    gap: 18px;
  }

  .detail p {
    margin: 0;
  }

  section {
    display: grid;
    gap: 8px;
  }

  h3 {
    margin: 0;
    font-size: var(--t-md);
  }

  .grow {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .grow > span:nth-child(2) {
    flex: 1;
  }

  .link,
  .row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .link .input {
    flex: 1 1 160px;
  }

  @media (max-width: 720px) {
    .tr {
      grid-template-columns: 1fr 1.4fr;
    }

    .hide-sm {
      display: none;
    }
  }
</style>
